import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, COLORS, REDUCED_MOTION } from '../../../../theme/tokens';

/* The finished-clip player: the video framed in its aspect ratio, then a ready line and the
   Download action. Colours come only from theme tokens. */
export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** Aspect-locked frame. Landscape fills the column; portrait is capped so it doesn't dominate. */
export const Frame = styled.div<{ $ratio: number }>`
  position: relative;
  width: 100%;
  max-width: ${(p) => (p.$ratio >= 1 ? '100%' : '300px')};
  aspect-ratio: ${(p) => p.$ratio};
  margin-inline: auto;
  border-radius: 14px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const Video = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: ${COLORS.pageBg};
`;

/** The click-to-play preview: a poster (or the clip's first frame) under a play badge, filling the
 *  Frame until the viewer starts playback. */
export const PreviewButton = styled.button.attrs({ type: 'button' as const })`
  position: absolute;
  inset: 0;
  padding: 0;
  border: none;
  cursor: pointer;
  background: ${COLORS.pageBg};
`;

export const PosterImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/** Fallback poster when the caller passes no still: the clip itself, metadata-only, showing frame 1. */
export const PosterVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/** Round play badge over the preview (pure-CSS triangle, no icon dependency); brightens on hover. */
export const PlayBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.overlaySoft};
  border: 1px solid ${COLORS.onAccent};
  pointer-events: none;
  transition:
    transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 10px 0 10px 17px;
    border-color: transparent transparent transparent ${COLORS.onAccent};
    margin-left: 4px;
  }

  ${PreviewButton}:hover & {
    background: ${COLORS.accent};
    transform: translate(-50%, -50%) scale(1.06);
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ReadyText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.heading};
`;
